import React, { useState } from 'react';
import { useGetProfessions } from '../../hooks/genericHooks';
import StandardTable from '../../components/common/StandardTable';
import { Card, Form, Input, Modal, Popconfirm, Row, Col, Select, Space, Typography, message } from 'antd';
import { PlusOutlined, SearchOutlined, SolutionOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnType } from 'antd/es/table';
import type { CorProfessions } from '../../types';
import { deleteData, insertData, updateData } from '../../services/api';
import Button from '../../components/common/Button';

const { Title } = Typography;

// Interface for filters
interface Filters {
  labor_type: string;
  profession: string;
}

const CorProfessionsList: React.FC = () => {
  const [filters, setFilters] = useState<Filters>({ labor_type: '', profession: '' });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingProfession, setEditingProfession] = useState<CorProfessions | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [addForm] = Form.useForm<{ labor_type: CorProfessions['labor_type']; profession: string }>();

  // --- Data Fetching ---
  // Use the generic hook to fetch profession data
  const { data: professionsData, isLoading, isError, error, refetch } = useGetProfessions();
  // Extract the actual list of professions, default to empty array if data is not yet loaded or fetch failed
  const professionList = professionsData?.data || [];

  const handleOpenAddModal = () => {
    addForm.resetFields();
    setEditingProfession(null);
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setEditingProfession(null);
  };

  const handleCreateProfession = async () => {
    try {
      const values = await addForm.validateFields();
      setIsSaving(true);

      if (editingProfession?.id) {
        await updateData('cor_professions', editingProfession.id, {
          labor_type: values.labor_type,
          profession: values.profession,
        });
        message.success('Profession updated');
      } else {
        await insertData('cor_professions', {
          labor_type: values.labor_type,
          profession: values.profession,
        });
        message.success('Profession created');
      }

      setIsAddModalOpen(false);
      await refetch();
    } catch (e: any) {
      // If validation fails, antd throws; do not show a generic error toast in that case.
      if (e?.errorFields) return;
      message.error(e?.message || 'Failed to create profession');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenEditModal = (record: CorProfessions) => {
    setEditingProfession(record);
    addForm.setFieldsValue({
      labor_type: record.labor_type,
      profession: record.profession,
    });
    setIsAddModalOpen(true);
  };

  const handleDeleteProfession = async (record: CorProfessions) => {
    const id = Number(record?.id);
    if (!Number.isFinite(id)) {
      message.error('Invalid profession id');
      return;
    }

    try {
      setDeletingId(id);
      await deleteData('cor_professions', 'id', id);
      message.success('Profession deleted');
      await refetch();
    } catch (e: any) {
      message.error(e?.message || 'Failed to delete profession');
    } finally {
      setDeletingId(null);
    }
  };

  // --- Error Handling ---
  // Display an error message if the fetch fails
  if (isError) {
    console.error("Failed to fetch professions:", error);
    message.error(`Could not load profession data: ${error?.message || 'Unknown error'}`);
    // Optionally return an error display component here
  }

  // --- Filtering ---
  // Filter the profession list based on the search input
  const filteredProfessions = professionList.filter((profession) => {
    // Filter by labor_type
    if (filters.labor_type && filters.labor_type.trim() !== '') {
      const laborTypeTerm = filters.labor_type.toLowerCase();
      if (!(profession.labor_type?.toLowerCase() || '').includes(laborTypeTerm)) {
        return false;
      }
    }
    // Filter by profession name
    if (filters.profession && filters.profession.trim() !== '') {
      const professionTerm = filters.profession.toLowerCase();
      if (!(profession.profession?.toLowerCase() || '').includes(professionTerm)) {
        return false;
      }
    }

    // If no filters active or if the item matches all active filters, include it
    return true;
  });

  // --- Table Columns ---
  const columns: ColumnType<CorProfessions>[] = [
    {
      title: 'Labor Type',
      dataIndex: 'labor_type',
      key: 'labor_type',
      sorter: (a, b) => (a.labor_type || '').localeCompare(b.labor_type || ''),
      width: 200,
    },
    {
      title: 'Profession',
      dataIndex: 'profession',
      key: 'profession',
      sorter: (a, b) => (a.profession || '').localeCompare(b.profession || ''),
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 110,
      fixed: 'right',
      render: (_: any, record: CorProfessions) => {
        const id = Number(record?.id);
        const isValidId = Number.isFinite(id);

        return (
          <Space size={4}>
            <Button action="edit"
              type="primary"
              icon={<EditOutlined />}
              onClick={() => handleOpenEditModal(record)}
              size="small"
              title="Edit Profession"
              disabled={!isValidId}
            />
            <Popconfirm
              title="Are you sure you want to delete this profession?"
              onConfirm={() => isValidId && handleDeleteProfession(record)}
              okText="Yes"
              cancelText="No"
              disabled={!isValidId || (deletingId === id)}
            >
              <Button action="delete"
                type="link"
                danger
                icon={<DeleteOutlined />}
                loading={deletingId === id}
                size="small"
                title="Delete Profession"
                disabled={!isValidId}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // --- Render Component ---
  return (
    <Card>
      {/* Header Row: Title */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <SolutionOutlined style={{ marginRight: 8 }} />
            Profession List
          </Title>
        </Col>
        <Col>
          <Button action="add" type="primary" icon={<PlusOutlined />} onClick={handleOpenAddModal}>
            Add Profession
          </Button>
        </Col>
      </Row>

      {/* Filter Row: Search Input */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Input
            placeholder="Search Labor Type..."
            prefix={<SearchOutlined />}
            value={filters.labor_type}
            // Update filter state on input change
            onChange={(e) => setFilters((prev) => ({ ...prev, labor_type: e.target.value }))}
            allowClear // Show clear button (x)
            disabled={isLoading} // Disable input while data is loading
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Input
            placeholder="Search Profession..."
            prefix={<SearchOutlined />}
            value={filters.profession}
            // Update filter state on input change
            onChange={(e) => setFilters((prev) => ({ ...prev, profession: e.target.value }))}
            allowClear // Show clear button (x)
            disabled={isLoading} // Disable input while data is loading
          />
        </Col>
      </Row>

      {/* Data Table */}
      <StandardTable
        dataSource={filteredProfessions}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={{ pageSize: 20 }}
        enableExport={true}
      />

      <Modal
        title={editingProfession ? 'Edit Profession' : 'Add Profession'}
        open={isAddModalOpen}
        onOk={handleCreateProfession}
        okText={editingProfession ? 'Save' : 'Create'}
        confirmLoading={isSaving}
        onCancel={handleCloseAddModal}
        destroyOnClose
      >
        <Form form={addForm} layout="vertical" preserve={false}>
          <Form.Item
            label="Labor Type"
            name="labor_type"
            rules={[{ required: true, message: 'Please select labor type' }]}
          >
            <Select
              placeholder="Select labor type"
              options={[
                { value: 'staff admin', label: 'staff admin' },
                { value: 'staff technical', label: 'staff technical' },
                { value: 'direct', label: 'direct' },
                { value: 'service', label: 'service' },
              ]}
              showSearch
              filterOption={(input, option) =>
                ((option?.label as string) || '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            label="Profession"
            name="profession"
            rules={[{ required: true, message: 'Please enter profession' }]}
          >
            <Input placeholder="e.g. Electrician" maxLength={120} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default CorProfessionsList;
